import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient, API_ENDPOINTS, ApiError } from '../lib';
import { sseClient } from '../lib/sseClient';
import type { UserRole } from '../lib/roles';
import { getDeviceId } from '../lib/deviceId';

interface User {
  id: number;
  userNumber?: string;
  username: string;
  email: string;
  role: UserRole;
  points?: number;
  createdAt?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (username: string, email: string, password: string, inviteCode: string) => Promise<{ success: boolean; error?: string }>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  updateProfile: (username: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  verifyAuth: () => Promise<boolean>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,
      
      clearError: () => set({ error: null }),
      
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await apiClient.post<{ user: User; token: string }>(
            API_ENDPOINTS.auth.login,
            { email, password, deviceId: getDeviceId() }
          );

          set({
            user: data.user,
            token: data.token,
            isLoading: false,
          });
          return { success: true };
        } catch (error) {
          let errorMessage = '登录失败，请稍后重试';

          if (error instanceof ApiError && error.code === 'DEVICE_LIMIT_REACHED') {
            errorMessage = '该账号已在三台设备上登录，请先退出其中一台设备';
          } else if (error instanceof Error) {
            const message = error.message.toLowerCase();
            if (message.includes('invalid') && message.includes('email') && message.includes('password')) {
              errorMessage = '邮箱或密码错误，请检查后重试';
            } else if (message.includes('user not found') || message.includes('user not exist')) {
              errorMessage = '该用户不存在，请先注册';
            } else if (message.includes('email') && message.includes('not')) {
              errorMessage = '该邮箱未注册，请先注册';
            } else if (message.includes('wrong') || message.includes('incorrect')) {
              errorMessage = '密码错误，请检查后重试';
            } else if (message.includes('too many') || message.includes('rate limit')) {
              errorMessage = '登录尝试次数过多，请稍后再试';
            } else if (message.includes('network') || message.includes('fetch')) {
              errorMessage = '网络连接失败，请检查网络';
            }
          }

          set({
            error: errorMessage,
            isLoading: false,
          });
          return { success: false, error: errorMessage };
        }
      },
      
      register: async (username, email, password, inviteCode) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await apiClient.post<{ user: User; token: string }>(
            API_ENDPOINTS.auth.register,
            { username, email, password, inviteCode, deviceId: getDeviceId() }
          );

          set({
            user: data.user,
            token: data.token,
            isLoading: false,
          });
          return { success: true };
        } catch (error) {
          let errorMessage = '注册失败，请稍后重试';
          
          if (error instanceof ApiError) {
            if (error.details && error.details.length > 0) {
              errorMessage = error.details.join('；');
            } else {
              const message = error.message.toLowerCase();
              if (message.includes('email') && message.includes('exist') && message.includes('already')) {
                errorMessage = '该邮箱已被注册，请直接登录或使用其他邮箱';
              } else if (message.includes('username') && message.includes('exist') && message.includes('already')) {
                errorMessage = '该用户名已被使用，请选择其他用户名';
              } else if (message.includes('邀请码')) {
                errorMessage = error.message;
              } else if (message.includes('too many') || message.includes('rate limit')) {
                errorMessage = '注册请求过于频繁，请稍后再试';
              } else if (message.includes('network') || message.includes('fetch')) {
                errorMessage = '网络连接失败，请检查网络';
              }
            }
          } else if (error instanceof Error) {
            const message = error.message.toLowerCase();
            if (message.includes('network') || message.includes('fetch')) {
              errorMessage = '网络连接失败，请检查网络';
            }
          }

          set({
            error: errorMessage,
            isLoading: false,
          });
          return { success: false, error: errorMessage };
        }
      },

      changePassword: async (oldPassword, newPassword) => {
        const { token } = get();
        if (!token) {
          return { success: false, error: '未登录，请先登录' };
        }

        set({ isLoading: true, error: null });
        try {
          await apiClient.put(
            API_ENDPOINTS.auth.password,
            { oldPassword, newPassword },
            token
          );

          set({ user: null, token: null, isLoading: false });
          return { success: true };
        } catch (error) {
          let errorMessage = '修改密码失败，请稍后重试';

          if (error instanceof ApiError) {
            if (error.details && error.details.length > 0) {
              errorMessage = error.details.join('；');
            } else {
              const message = error.message.toLowerCase();
              if (message.includes('invalid old password')) {
                errorMessage = '原密码不正确，请重新输入';
              } else if (message.includes('password')) {
                errorMessage = error.message;
              } else if (message.includes('network') || message.includes('fetch')) {
                errorMessage = '网络连接失败，请检查网络';
              }
            }
          } else if (error instanceof Error) {
            const message = error.message.toLowerCase();
            if (message.includes('network') || message.includes('fetch')) {
              errorMessage = '网络连接失败，请检查网络';
            }
          }

          set({
            error: errorMessage,
            isLoading: false,
          });
          return { success: false, error: errorMessage };
        }
      },

      updateProfile: async (username) => {
        const { token } = get();
        if (!token) {
          return { success: false, error: '未登录，请先登录' };
        }

        set({ isLoading: true, error: null });
        try {
          const { data } = await apiClient.put<User>(
            API_ENDPOINTS.auth.updateProfile,
            { username },
            token
          );

          set({
            user: {
              id: data.id,
              userNumber: data.userNumber,
              username: data.username,
              email: data.email,
              role: data.role,
              points: data.points,
              createdAt: data.createdAt,
            },
            isLoading: false,
          });
          return { success: true };
        } catch (error) {
          let errorMessage = '更新资料失败，请稍后重试';

          if (error instanceof ApiError) {
            if (error.details && error.details.length > 0) {
              errorMessage = error.details.join('；');
            } else {
              const message = error.message.toLowerCase();
              if (message.includes('taken') || message.includes('exist')) {
                errorMessage = '该用户名已被使用，请选择其他用户名';
              } else if (message.includes('network') || message.includes('fetch')) {
                errorMessage = '网络连接失败，请检查网络';
              }
            }
          }

          set({ error: errorMessage, isLoading: false });
          return { success: false, error: errorMessage };
        }
      },
      
      logout: async () => {
        const { token } = get();
        sseClient.disconnect();
        set({ user: null, token: null });

        if (!token) {
          return;
        }

        try {
          await apiClient.post<void>(API_ENDPOINTS.auth.logout, undefined, token, 5000);
        } catch (error) {
          console.warn('Failed to end the login session on the server', error);
        }
      },
      
      verifyAuth: async () => {
        const { token } = get();
        
        if (!token) {
          set({ user: null });
          return false;
        }
        
        try {
          const { data: userData } = await apiClient.get<User>(
            API_ENDPOINTS.auth.profile,
            token
          );
          
          set({
            user: {
              id: userData.id,
              userNumber: userData.userNumber,
              username: userData.username,
              email: userData.email,
              role: userData.role,
              points: userData.points,
              createdAt: userData.createdAt,
            },
          });
          return true;
        } catch {
          set({ user: null, token: null });
          return false;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ token: state.token, user: state.user }),
    }
  )
);
